/**
 * F-022: React Query hook for admin order detail.
 *
 * Wraps `getAdminOrderDetail` with `useQuery`. Query key scoped to the
 * orderId. Disabled when orderId is falsy. `retry: false` — RLS/not-found
 * errors must not be retried (they will always fail again).
 */
import { useQuery } from '@tanstack/react-query';
import { getAdminOrderDetail } from '@dukanti/shared';
import type { AdminOrderDetail } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

export interface UseAdminOrderDetailResult {
  readonly data: AdminOrderDetail | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly refetch: () => void;
}

/**
 * Fetch full order detail for the admin detail view.
 *
 * staleTime: 0 — order status can change externally (another tab may have
 * fired a transition). Always refetch on window focus.
 * retry: false — PGRST116 (RLS/not-found) errors must not retry.
 */
export function useAdminOrderDetail(orderId: string | undefined): UseAdminOrderDetailResult {
  const q = useQuery({
    queryKey: ['admin-order-detail', orderId] as const,
    enabled: !!orderId,
    staleTime: 0,
    retry: false,
    queryFn: () => getAdminOrderDetail(supabase, orderId as string),
  });

  return {
    data: q.data,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: () => void q.refetch(),
  };
}
